'use client';

import posthog from 'posthog-js';
import { PostHogProvider as PHProvider } from 'posthog-js/react';
import { Suspense, useEffect } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import {
  buildPostHogInitOptions,
  buildPostHogPageviewUrl,
  shouldInitializePostHog,
  type PostHogEnv,
} from '@/lib/analytics';

const POSTHOG_KEY = process.env.NEXT_PUBLIC_POSTHOG_KEY;
const postHogEnv: PostHogEnv = {
  NEXT_PUBLIC_POSTHOG_HOST: process.env.NEXT_PUBLIC_POSTHOG_HOST,
  NEXT_PUBLIC_POSTHOG_KEY: POSTHOG_KEY,
};
// Analytics is production-only (kasbly.com / app / docs). In local dev the
// public key may be present but PostHog's CDN is blocked by our CSP — skip it.
const IS_POSTHOG_ENABLED =
  shouldInitializePostHog(postHogEnv) && process.env.NODE_ENV === 'production';

/**
 * Captures `$pageview` on every client-side navigation. Next's App Router does
 * not emit page loads the way the Pages Router did, so PostHog's automatic
 * pageview capture is disabled and we fire it manually on path/query changes.
 */
function PostHogPageView() {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    if (!IS_POSTHOG_ENABLED || !pathname) return;
    const qs = searchParams?.toString();
    const url = buildPostHogPageviewUrl(window.origin, pathname, qs);
    posthog.capture('$pageview', { $current_url: url });
  }, [pathname, searchParams]);

  return null;
}

export function PostHogProvider({ children }: { children: React.ReactNode }) {
  useEffect(() => {
    if (!IS_POSTHOG_ENABLED || !POSTHOG_KEY) return;
    posthog.init(POSTHOG_KEY, buildPostHogInitOptions(postHogEnv));
  }, []);

  // No key configured (e.g. local dev without analytics) → render children as-is.
  if (!IS_POSTHOG_ENABLED) return <>{children}</>;

  return (
    <PHProvider client={posthog}>
      <Suspense fallback={null}>
        <PostHogPageView />
      </Suspense>
      {children}
    </PHProvider>
  );
}
